// SF2 and SF3's per-ASIN graph.
//
// The tables answer "which products draw traffic"; this answers "what has
// happened to this one". Sessions and page views share the chart because the
// gap between them is the finding — a product at three views per session is
// being compared and revisited before a decision.
//
// A window that was never collected is absent from the series rather than
// drawn at zero. A gap in collection and a week with no visitors look
// identical on a chart, and only one of them is about the product.
import React, { useState } from 'react';
import s from '../Visibility.module.css';
import { num } from '../format';

export default function ProductTraffic({ data, loading, onLoad }) {
  const [asin, setAsin] = useState('');

  const submit = (e) => {
    e.preventDefault();
    const value = asin.trim().toUpperCase();
    if (value) onLoad(value);
  };

  const points = data?.series || [];
  const peak = points.reduce((m, p) => Math.max(m, p.page_views || 0, p.sessions || 0), 0) || 1;

  return (
    <section className={s.panel} style={{ marginTop: 18 }}>
      <h4 className={s.sectionTitle}>One product over time</h4>
      <form className={s.controls} onSubmit={submit} style={{ marginBottom: 14 }}>
        <label className={s.field}>
          <span className={s.label}>ASIN</span>
          <input className={s.input} value={asin} placeholder="B08XXXXXXX"
                 onChange={(e) => setAsin(e.target.value)} />
        </label>
        <button className={s.secondary} type="submit" disabled={loading || !asin.trim()}>
          {loading ? 'Loading…' : 'Show traffic'}
        </button>
      </form>

      {!data && !loading && (
        <p className={s.hint}>
          Enter an ASIN to see its sessions and page views across the collected windows.
        </p>
      )}

      {data && !points.length && (
        <p className={s.hint}>
          No Sales &amp; Traffic figures for {data.asin} in the last{' '}
          {data.windows_requested} windows. {data.windows_collected === 0
            && 'None of those windows has been collected for this product.'}
        </p>
      )}

      {points.length > 0 && (
        <>
          <div className={s.seriesChart}>
            {points.map((p) => (
              <div key={p.start_date} className={s.seriesCol}>
                <div className={s.seriesBars}>
                  <span className={s.barViews}
                        style={{ height: `${((p.page_views || 0) / peak) * 100}%` }}
                        title={`${num(p.page_views)} page views`} />
                  <span className={s.barSessions}
                        style={{ height: `${((p.sessions || 0) / peak) * 100}%` }}
                        title={`${num(p.sessions)} sessions`} />
                </div>
                <span className={s.seriesLabel}>{p.start_date.slice(5)}</span>
                <span className={s.seriesRatio}>
                  {p.views_per_session == null ? '—' : `${p.views_per_session}×`}
                </span>
              </div>
            ))}
          </div>
          <div className={s.seriesKey}>
            <span><i className={s.barSessions} /> sessions</span>
            <span><i className={s.barViews} /> page views</span>
            <span className={s.muted}>the number under each bar is views per session</span>
          </div>
          <p className={s.hint} style={{ marginTop: 10 }}>
            {data.summary.windows < 2
              ? data.summary.note
              : <>
                  {num(data.summary.total_sessions)} sessions across{' '}
                  {data.summary.windows} collected windows
                  {data.summary.change != null && <>
                    {' '}· {data.summary.change >= 0 ? 'up' : 'down'}{' '}
                    {Math.abs(data.summary.change * 100).toFixed(0)}% from first to last
                  </>}
                  {data.windows_collected < data.windows_requested && <>
                    {' '}· {data.windows_requested - data.windows_collected} of the last{' '}
                    {data.windows_requested} windows have no figure for this product and are
                    left out rather than drawn as zero
                  </>}
                </>}
          </p>
        </>
      )}
    </section>
  );
}
